/*
  One Tooth World — shared variant picking (quick view and the product page).
  OTW.variants.init(root, { variants, onChange }) wires the option controls inside `root`:
    input[type=radio][data-option-index="0|1|2"]  (pill / swatch types)
    select[data-option-index="0|1|2"]             (dropdown type)
  It:
    - finds the matching variant for the selected option values,
    - marks option values with no available variant (computed from the product's variants JSON)
      as .is-unavailable (crossed out, still selectable); in a <select> the option text gets a suffix,
    - writes the selected value into [data-otw-option-value="<index>"],
    - calls onChange(variant | undefined, selectedValues).
  Returns { selected(), variant(), select(index, value), refresh() }.
*/
const OTW = (window.OTW = window.OTW || {});

OTW.variants = {
  find(variants, selected) {
    return variants.find((v) => selected.every((value, i) => v.options[i] === value));
  },

  init(root, { variants = [], onChange } = {}) {
    const controls = [...root.querySelectorAll('[data-option-index]')].filter(
      (el) => el.matches('input[type="radio"]') || el.matches('select')
    );
    const radios = controls.filter((el) => el.matches('input'));
    const selects = controls.filter((el) => el.matches('select'));
    const count = new Set(controls.map((el) => el.dataset.optionIndex)).size;

    const selected = () => {
      const values = [];
      for (let i = 0; i < count; i++) {
        const select = selects.find((el) => Number(el.dataset.optionIndex) === i);
        if (select) {
          values.push(select.value || undefined);
          continue;
        }
        const checked = radios.find((el) => Number(el.dataset.optionIndex) === i && el.checked);
        values.push(checked ? checked.value : undefined);
      }
      return values;
    };

    const current = () => {
      const values = selected();
      if (values.some((v) => v === undefined)) return undefined;
      return OTW.variants.find(variants, values);
    };

    const isAvailable = (values, index, value) => {
      const probe = values.map((v, i) => (i === index ? value : v));
      return variants.some((v) => v.available && probe.every((p, i) => p === undefined || v.options[i] === p));
    };

    const refresh = () => {
      const values = selected();
      radios.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const ok = isAvailable(values, index, input.value);
        input.classList.toggle('is-unavailable', !ok);
        input.closest('label, .otw-opt')?.classList.toggle('is-unavailable', !ok);
      });
      selects.forEach((select) => {
        const index = Number(select.dataset.optionIndex);
        [...select.options].forEach((option) => {
          if (!option.value) return;
          const ok = isAvailable(values, index, option.value);
          const base = option.dataset.label || option.value;
          option.classList.toggle('is-unavailable', !ok);
          option.textContent = ok ? base : `${base} ✕`;
        });
      });
      values.forEach((value, i) => {
        const out = root.querySelector(`[data-otw-option-value="${i}"]`);
        if (out) out.textContent = value || '';
      });
      if (onChange) onChange(current(), values);
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches?.('input[type="radio"][data-option-index], select[data-option-index]')) refresh();
    });

    refresh();

    return {
      selected,
      variant: current,
      refresh,
      select(index, value) {
        const select = selects.find((el) => Number(el.dataset.optionIndex) === index);
        if (select) {
          select.value = value;
          refresh();
          return;
        }
        const input = radios.find((el) => Number(el.dataset.optionIndex) === index && el.value === value);
        if (input) {
          input.checked = true;
          refresh();
        }
      },
    };
  },
};
